/**
 * Marks a Verification Run in progress for one entity in localStorage, so a
 * reload or a second tab in the same browser shows it running and does not
 * start a duplicate. How a run ended is read from the record, not from here.
 *
 * It cannot see runs started in another browser or by another user; the
 * entity service refuses those itself.
 */

/**
 * Longest a run can take. Outlives the run request's own 120s timeout, so a
 * second tab never starts duplicate lookups while the first is still waiting.
 */
export const VERIFICATION_RUN_MAX_MS = 130 * 1000;

export interface StoredVerificationRun {
  startedAt: number;
  finishedAt?: number;
}

const storageKey = (entityId: string): string =>
  `bite.businessVerification.${entityId}`;

export const verificationRunStorageKey = storageKey;

export const readVerificationRun = (
  entityId: string,
): StoredVerificationRun | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(entityId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.startedAt === 'number' ? parsed : null;
  } catch {
    return null;
  }
};

const write = (entityId: string, run: StoredVerificationRun) => {
  try {
    window.localStorage.setItem(storageKey(entityId), JSON.stringify(run));
  } catch {
    // Storage blocked or full: the run still works, it just is not shared.
  }
};

export const isVerificationRunActive = (
  run: StoredVerificationRun | null,
  now = Date.now(),
): boolean =>
  Boolean(
    run && !run.finishedAt && now - run.startedAt < VERIFICATION_RUN_MAX_MS,
  );

export const markVerificationRunStarted = (
  entityId: string,
  startedAt: number,
): void => write(entityId, { startedAt: startedAt });

/** Only the run that holds the record may close it; a newer run keeps its lock. */
export const markVerificationRunFinished = (
  entityId: string,
  startedAt: number,
): void => {
  const current = readVerificationRun(entityId);
  if (current && current.startedAt !== startedAt) return;
  write(entityId, { startedAt: startedAt, finishedAt: Date.now() });
};
